import { useTranslations } from "@jugl-web/utils";
import { EntityInvitationCard } from "@web-src/modules/entities/components/EntityInvitationCard";
import { useEntitiesListProvider } from "@web-src/modules/entities/providers/EntitiesListProvider/useEntitiesListProvider";
import { Button } from "@jugl-web/ui-kit";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import { useEffect, useState, useMemo } from "react";
import {
  EntityInvitationModel,
  InvitationLinkResponse,
} from "@jugl-web/rest-api";
import { useSelectEntity } from "@web-src/modules/entities/hooks/useSelectEntity";
import {
  saveItemToStorage,
  getStorageItem,
  LAST_JOINED_ENTITY_ID_KEY,
} from "@jugl-web/utils/storage";
import { useNavigateToEntityById } from "@web-src/modules/entities/hooks/useNavigateToEntityById";
import { SignUpFlowLayout } from "../SignUpFlowLayout";
import { SignUpFlowContentLayout } from "../../components/SignUpFlowContentLayout/SignUpFlowContentLayout";

export const SignUpInvitations = ({
  invitationLink,
}: {
  invitationLink?: InvitationLinkResponse;
}) => {
  const { t } = useTranslations();
  const {
    entitiesState,
    entityInvitesState: { entityInvites, isLoading: isInvitesLoading },
  } = useEntitiesListProvider();

  const { navigateToPage } = useNavigation();
  const { selectEntity } = useSelectEntity();
  const [invitations, setInvitations] = useState<
    Map<string, EntityInvitationModel & { module?: string }>
  >(new Map());
  const [hasCheckedInvitations, setHasCheckedInvitations] = useState(false);
  const navigateToEntityById = useNavigateToEntityById();

  const isMoreThanOneInvite = useMemo(
    () => invitations.size > 1,
    [invitations]
  );

  useEffect(() => {
    if (!entityInvites) return;

    setInvitations((prevInvitations) => {
      const updatedMap = new Map(prevInvitations);
      if (invitationLink) {
        const transformedInvitation = {
          id: invitationLink.id,
          entityId: invitationLink.entity.id,
          entityName: invitationLink.entity.name,
          entityDescription: null,
          entityImg: invitationLink.entity.img,
          inviterName: invitationLink.created_by.name,
          inviterAvatar: invitationLink.created_by.img,
          type: invitationLink.entity.type,
          invitationDate: "",
          module: "entity_link",
        };
        updatedMap.set(invitationLink.entity.id, transformedInvitation);
      } else {
        entityInvites.forEach((invite) => {
          updatedMap.set(invite.entityId, {
            ...invite,
          });
        });
      }

      return updatedMap;
    });
    setHasCheckedInvitations(true);
  }, [entityInvites, invitationLink]);

  const isAnyInvitationJoined = useMemo(
    () =>
      Array.from(invitations.values()).some((invitation) =>
        entitiesState.entities?.some(
          (entity) => entity.id === invitation.entityId
        )
      ),
    [invitations, entitiesState.entities]
  );

  useEffect(() => {
    if (
      !(entityInvites?.length === 0 && invitations.size === 0) ||
      invitationLink
    ) {
      return;
    }

    if (getStorageItem(LAST_JOINED_ENTITY_ID_KEY)) {
      const lastJoinedEntityId = getStorageItem(LAST_JOINED_ENTITY_ID_KEY);
      const lastJoinedEntity = entitiesState.entities?.find(
        (entity) => entity.id === lastJoinedEntityId
      );
      if (lastJoinedEntity) {
        navigateToEntityById(
          lastJoinedEntity.id,
          lastJoinedEntity.type === "org"
        );
        localStorage.removeItem(LAST_JOINED_ENTITY_ID_KEY);
      }
    } else {
      navigateToPage("authNoInvitations");
    }
  }, [
    isAnyInvitationJoined,
    entitiesState.entities,
    selectEntity,
    entityInvites?.length,
    invitations.size,
    navigateToPage,
    invitations,
    navigateToEntityById,
    invitationLink,
  ]);

  useEffect(() => {
    if (isInvitesLoading) return;

    if (!hasCheckedInvitations) return;

    if (invitations.size === 0) {
      navigateToPage("entryPoint");
    }
  }, [hasCheckedInvitations, invitations, isInvitesLoading, navigateToPage]);

  return (
    <SignUpFlowLayout
      type="invitations"
      isLoading={
        entitiesState.isLoading || entitiesState.isFetching || isInvitesLoading
      }
      onBackClick={() => {
        if (entitiesState.entities?.length) {
          navigateToPage("entryPoint");
        } else {
          navigateToPage("authSignUpPurpose");
        }
      }}
    >
      <SignUpFlowContentLayout
        title={t({
          id: "sign-up-invitations-page.title",
          defaultMessage: "You've been invited! 💌",
        })}
        subtitle={t({
          id: "sign-up-invitations-page.subtitle",
          defaultMessage: "Select a workspace below to join and get started.",
        })}
        description={
          <div className="flex h-[204px] w-full max-w-[566px] flex-col items-center gap-6 overflow-auto">
            {Array.from(invitations.values()).map((invitation) => (
              <EntityInvitationCard
                key={invitation.id}
                invitation={invitation}
                disableDelete
                redirectOnClick={
                  entitiesState.entities?.some(
                    (entity) => entity.id === invitation.entityId
                  ) || false
                }
                className="w-full p-[21px]"
                variant={
                  invitation.module === "entity_link"
                    ? "entity_link"
                    : "default"
                }
                isJoined={
                  entitiesState.entities?.some(
                    (entity) => entity.id === invitation.entityId
                  ) || false
                }
                onInvitationAcceptedSuccess={() => {
                  if (!isMoreThanOneInvite || invitationLink) {
                    navigateToEntityById(
                      invitation.entityId,
                      invitation.type === "org"
                    );
                    return;
                  }
                  saveItemToStorage(
                    LAST_JOINED_ENTITY_ID_KEY,
                    invitation.entityId
                  );
                }}
              />
            ))}
            {!isMoreThanOneInvite && !invitationLink && (
              <div className="pt-[32px] text-dark-600">
                {t({
                  id: "sign-up-invitations-page.no-invitations",
                  defaultMessage: "Don't see what you're looking for? ",
                })}
                <span
                  className="cursor-pointer pl-1 text-primary-600"
                  onClick={() =>
                    navigateToPage("authNewWorkspace", {
                      flow: "signup",
                    })
                  }
                >
                  {t({
                    id: "sign-up-invitations-page.create-workspace",
                    defaultMessage: "Create workspace",
                  })}
                </span>
              </div>
            )}
          </div>
        }
        footer={
          <>
            <div className="pt-[64px]">
              {isMoreThanOneInvite && (
                <Button
                  className="mx-auto w-full max-w-[300px]"
                  type="submit"
                  size="sm"
                  isDisabled={!isAnyInvitationJoined}
                  onClick={() => {
                    if (!entitiesState.entities?.[0]) {
                      return;
                    }
                    const lastJoinedEntityId = getStorageItem(
                      LAST_JOINED_ENTITY_ID_KEY
                    );
                    const lastJoinedEntity = entitiesState.entities?.find(
                      (entity) => entity.id === lastJoinedEntityId
                    );
                    if (lastJoinedEntity) {
                      navigateToEntityById(
                        lastJoinedEntity.id || entitiesState.entities?.[0].id,
                        true
                      );
                      localStorage.removeItem(LAST_JOINED_ENTITY_ID_KEY);
                    }
                  }}
                >
                  {t({
                    id: "common.continue",
                    defaultMessage: "Continue",
                  })}
                </Button>
              )}
            </div>
            {isMoreThanOneInvite && (
              <div className="pt-[32px] text-dark-600">
                {t({
                  id: "sign-up-invitations-page.no-invitations",
                  defaultMessage: "Don't see what you're looking for? ",
                })}
                <span
                  className="cursor-pointer pl-1 text-primary-600"
                  onClick={() =>
                    navigateToPage("authNewWorkspace", {
                      flow: "signup",
                    })
                  }
                >
                  {t({
                    id: "sign-up-invitations-page.create-workspace",
                    defaultMessage: "Create workspace",
                  })}
                </span>
              </div>
            )}
          </>
        }
      />
    </SignUpFlowLayout>
  );
};
